/**
 * RenderScheduler — on-demand rendering with a safety floor.
 *
 * The in-game GameLoop ticks at 60fps, but the GPU draw (shadows, env maps,
 * many materials) is what heats the phone. Rendering every frame while the
 * board is STATIC (betting idle, dice at rest, a paused scene) is pure waste
 * that drives the thermal throttle — and the throttle is what collapses the
 * frame rate and starves the audio thread (the "echoed sound + bad framerate").
 *
 * This scheduler lets the renderer run at FULL rate only while something is
 * actually animating, and drop to a slow IDLE heartbeat otherwise — cutting
 * sustained GPU load ~85% when nothing moves so the device stays cool.
 *
 * Two ways to express "something is animating":
 *   - acquire(): hold a lease for the duration of a continuous animation; call
 *     the returned release() when it ends. Refcounted — N overlapping leases
 *     keep one full-rate stream.
 *   - pulse(ms): keep full-rate rendering for the next `ms`. For discrete
 *     changes (an object added/removed), for input, and for per-frame-renewed
 *     sources (physics-awake, opponent drag, animated shaders) that just call
 *     pulse() every frame while active.
 *
 * SAFETY (the whole reason this is shaped this way): a MISSED activity source
 * does NOT freeze the frame. The idle heartbeat still renders every
 * ~IDLE_INTERVAL_MS, so any un-pulsed change appears within a frame or two,
 * never permanently. A LEAKED lease only over-renders (wastes power), never
 * freezes. When in doubt we render. Tune toward correctness, not power.
 */

/** Idle heartbeat rate when nothing is animating. ~8fps = a missed change shows
 *  in ≤125ms (imperceptible) while GPU load drops ~85% vs 60fps. */
const IDLE_FPS = 8;
const IDLE_INTERVAL_MS = 1000 / IDLE_FPS;

/** Default tail kept rendering after a discrete change / a lease release, so the
 *  last few frames of motion settle before we drop to the heartbeat. */
const DEFAULT_PULSE_MS = 250;

/** Input keeps the renderer hot a little longer so gaps between move events
 *  (drag, pickup, betting) don't drop to the heartbeat mid-gesture. */
export const INPUT_PULSE_MS = 600;

/** Ceiling on shadow-map staleness while frames are being drawn. The shadow
 *  pass (VSM depth render + blur) only re-runs when a caster is marked dirty —
 *  but a MISSED mover must degrade to slightly-late shadows, never frozen ones.
 *  A drawn frame whose shadows are older than this re-renders them anyway, so
 *  an unmarked mover's shadow lags ≤150ms instead of sticking. Same philosophy
 *  as the idle heartbeat: tune toward correctness, not power. */
const SHADOW_MAX_STALENESS_MS = 150;

class RenderScheduler {
  private leases = 0;
  /** performance.now() timestamp until which we render at full rate. */
  private activeUntil = 0;
  /** Last time a frame was actually drawn (active OR heartbeat). */
  private lastRenderAt = 0;
  /** A shadow caster (dice, bill, wallet) or shadow light moved since the last
   *  shadow-map render. Starts true so the first frame always builds the map. */
  private shadowsDirty = true;
  /** Last time the shadow map was actually re-rendered. */
  private lastShadowRenderAt = 0;

  /**
   * Hold a render lease for a continuous animation. Returns an idempotent
   * release(); calling it twice is a no-op. On release we add a short tail so
   * the final frame(s) of the animation settle.
   */
  acquire(): () => void {
    this.leases++;
    let released = false;
    return () => {
      if (released) return;
      released = true;
      if (this.leases > 0) this.leases--;
      this.pulse(DEFAULT_PULSE_MS);
    };
  }

  /** Keep rendering at full rate until now + ms. Cheap; safe to call per frame. */
  pulse(ms: number = DEFAULT_PULSE_MS): void {
    const until = performance.now() + ms;
    if (until > this.activeUntil) this.activeUntil = until;
  }

  /** Is full-rate work happening right now? */
  isActive(now: number = performance.now()): boolean {
    return this.leases > 0 || now < this.activeUntil;
  }

  /**
   * Render gate, called once per frame by the render driver. Returns true when
   * this frame should draw: every frame while active, else only at the idle
   * heartbeat interval. Updates the internal "last drawn" clock as a side
   * effect, so the caller must draw whenever this returns true.
   */
  shouldRender(now: number = performance.now()): boolean {
    if (this.isActive(now)) {
      this.lastRenderAt = now;
      return true;
    }
    if (now - this.lastRenderAt >= IDLE_INTERVAL_MS) {
      this.lastRenderAt = now;
      return true;
    }
    return false;
  }

  /**
   * A shadow caster (dice, bill, wallet) or a shadow-casting light changed —
   * the next drawn frame must re-render the shadow map. Cheap; safe to call
   * per frame from continuous movers (mirrors pulse()).
   */
  markShadowsDirty(): void {
    this.shadowsDirty = true;
  }

  /**
   * Shadow gate, called by the render driver on a frame it is about to draw.
   * True when the shadow map should re-render this frame: a caster was marked
   * dirty, or the map is older than the staleness ceiling (self-heal for any
   * missed mover). Consumes the dirty flag — the caller must re-render the
   * shadow map whenever this returns true.
   */
  shouldRenderShadows(now: number = performance.now()): boolean {
    if (this.shadowsDirty || now - this.lastShadowRenderAt >= SHADOW_MAX_STALENESS_MS) {
      this.shadowsDirty = false;
      this.lastShadowRenderAt = now;
      return true;
    }
    return false;
  }

  /** Number of held leases (debug / diagnostics). */
  get activeLeases(): number {
    return this.leases;
  }
}

/** App-wide singleton. Import and call from anywhere that animates the 3D world. */
export const renderScheduler = new RenderScheduler();
